/**
 * A coupon code, typed by the customer and handed to 1C.
 *
 * Nothing here checks it against anything: the server stores it on the order
 * and 1C receives it as `coupon` on the reservation and the filing. **Any
 * shape**: it was twelve letters and digits, and real coupons turned out
 * shorter, so the only rules are «not empty» and `COUPON_MAX` — the same two
 * `zCoupon` holds, because a code the server refuses refuses the whole order.
 *
 * **A scanner fills it too**, whether or not the sheet is open: any scan while
 * this screen is up and no coupon is applied is the coupon. Nothing else on
 * the payment screen reads a scan — a product is the menu's and a badge the
 * welcome screen's. `services/scan-claim.ts` says why that needs asking for.
 *
 * **It is fixed once the order exists.** `orders.create` is idempotent on the
 * order id and returns the order it already has, so a coupon changed after the
 * first payment attempt would be drawn here and never reach 1C.
 */
import { useEffect, useState } from 'react'
import { Button, Sheet } from '@/components/ui'
import { Keyboard } from '@/components/keyboard'
import { useT } from '@/i18n'
import { claimScans } from '@/services/scan-claim'
import { AppliedExtra, PaymentExtra } from './payment-extra'

/** `zCoupon`'s ceiling. */
export const COUPON_MAX = 64

export function Coupon({
  value,
  onChange,
  locked
}: {
  value: string | null
  onChange: (coupon: string | null) => void
  /** The order has been posted; see the note above. */
  locked: boolean
}) {
  const t = useT()
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState('')

  const apply = (code: string): boolean => {
    const trimmed = code.trim()
    if (trimmed.length === 0 || trimmed.length > COUPON_MAX) return false
    onChange(trimmed)
    setDraft('')
    setOpen(false)
    return true
  }

  const waiting = !locked && value === null
  useEffect(() => {
    if (!waiting) return
    // With the sheet closed. With it open the field below has the focus, and
    // `watchWedge` leaves a focused input alone — the scan types into it.
    return claimScans((code) => {
      const trimmed = code.trim()
      if (trimmed.length === 0 || trimmed.length > COUPON_MAX) return false
      onChange(trimmed)
      setDraft('')
      setOpen(false)
      return true
    })
  }, [waiting, onChange])

  if (value !== null) {
    return (
      <AppliedExtra label={t('payment.coupon')} {...(locked ? {} : { onRemove: () => onChange(null) })}>
        <p className="display-upright truncate text-[length:var(--text-kiosk)] tracking-wider tabular-nums">{value}</p>
      </AppliedExtra>
    )
  }

  return (
    <>
      <PaymentExtra
        icon="ticket"
        label={t('payment.coupon')}
        hint={t('payment.couponRowHint')}
        unavailable={locked ? t('payment.couponLocked') : null}
        onOpen={() => setOpen(true)}
      />

      <Sheet open={open} onClose={() => setOpen(false)} labelledBy="coupon-title">
        <div className="flex flex-col gap-7 p-10">
          <h2 id="coupon-title" className="display text-[length:var(--text-kiosk-lg)] uppercase">
            {t('payment.couponCode')}
          </h2>

          {/*
            A real field, focused as the sheet opens, and not a paragraph. As a
            paragraph nothing could type into it: a physical keyboard had no
            target, and the scanner's keystrokes went to `watchWedge`, which
            drops anything slower than a scan. Focused, both type here and the
            scanner's Enter applies the code. `inputMode="none"` keeps
            Windows' touch keyboard away — the keys below are the keyboard, and
            they press on `pointerdown` with the default prevented, so tapping
            one never takes the focus off this field.
          */}
          <input
            // biome-ignore lint/a11y/noAutofocus: the scanner and a physical keyboard need a target the moment the sheet opens — see above
            autoFocus
            inputMode="none"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            aria-labelledby="coupon-title"
            maxLength={COUPON_MAX}
            placeholder="—"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return
              event.preventDefault()
              apply(draft)
            }}
            className="display w-full rounded-[var(--radius-card)] border-2 border-[var(--color-line)] bg-[var(--color-paper-sunk)] px-8 py-6 text-[length:var(--text-kiosk-xl)] tabular-nums outline-none placeholder:text-[var(--color-ink-ghost)] focus:border-[var(--color-brand)]"
          />
          <p className="text-[length:var(--text-kiosk-sm)] text-[var(--color-ink-soft)]">{t('payment.couponHint')}</p>

          <Keyboard
            code
            onKey={(character) => {
              setDraft((current) => (current + character).slice(0, COUPON_MAX))
            }}
            onBackspace={() => setDraft((current) => current.slice(0, -1))}
            onClear={() => setDraft('')}
          />

          <div className="flex items-center gap-5">
            <Button variant="text" className="text-[var(--color-ink-faint)]" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              size="large"
              className="ml-auto uppercase"
              disabled={draft.trim().length === 0}
              onClick={() => apply(draft)}
            >
              {t('payment.couponApply')}
            </Button>
          </div>
        </div>
      </Sheet>
    </>
  )
}
